import CloseIcon from '@mui/icons-material/Close';
import { Typography } from '@mui/joy';
import {
  Box,
  Paper,
  Stack,
  Modal,
  Fade,
  Tabs,
  Tab,
  IconButton,
} from '@mui/material';
import { LineChart } from '@mui/x-charts';
import dayjs from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import useSWR from 'swr';

import RequestForInformation from './requestForInformation';

import { intlFormatRfiStatus } from '../../../constants/reportsRfiIntl';
import { useLocalizedDepartmentData } from '../../../hooks/fetcher/useDepartmentData';
import {
  KeyProps,
  ReportLineChartProps,
  ListRFIByDepartmentProps,
  FilterProps,
  ReportFilters,
  LineChartDataset,
  ListRFIProps,
  StatusObject,
  DepartmentObject,
  LineChartObjectByDepartment,
  RFIByStatusColorType,
} from '../../../interfaces/ReportsRFI';
import {
  getUrlString,
  useDashoardFilter,
} from '../../../Page/customer/customerDashboard';
import LoadingComponent from '../../common/loadingComponent';
import NoDataFound from '../../common/noDataFound';

import { Button } from '~/components/ui/button';
import { Card } from '~/components/ui/card';
import { Option, Select } from '~/components/ui/select';
import { usePartnerContext } from '~/contexts/partner/partnerContext';
import { CheckMetricWrapper } from '~/pages/customer/dashboard/checkMetricWrapper';

const headerText = {
  fontSize: '18px',
  fontWeight: 500,
};

const ReportsLineChart = ({
  reportsDataList,
  keyToLabel,
  colors,
}: ReportLineChartProps) => {
  const intl = useIntl();

  return (
    <LineChart
      margin={{ top: 75 }}
      xAxis={[
        {
          scaleType: 'point',
          data: reportsDataList,
          dataKey: 'label',
          valueFormatter: (item) =>
            item.label
              ? item.label
              : intl.formatMessage({ defaultMessage: 'No data' }),
        },
      ]}
      yAxis={[
        {
          tickMaxStep: 100,
          tickMinStep: 1,
        },
      ]}
      series={Object.keys(keyToLabel).map((key) => ({
        type: 'line',
        curve: 'linear',
        dataKey: key,
        label: intlFormatRfiStatus(keyToLabel[key]),
        color: colors[key],
        showMark: ({ value }) => value !== 0,
      }))}
      dataset={reportsDataList}
      height={400}
      slotProps={{
        legend: {
          position: {
            vertical: 'top',
            horizontal: 'left',
          },
          labelStyle: {
            fontSize: 13,
          },
          itemMarkWidth: 12,
          itemMarkHeight: 12,
        },
      }}
    />
  );
};

const ListRFIByDepartment = ({
  open,
  handleClose,
  departments,
}: ListRFIByDepartmentProps) => {
  const tabs: string[] = Object.values(departments);
  const [selectedTab, setSelectedTab] = useState(tabs[0]);
  const { getLocalizedDepartmentName } = useLocalizedDepartmentData();

  const handleTabChange = (_: React.SyntheticEvent, newValue: string) => {
    setSelectedTab(newValue);
  };

  return (
    <Modal
      open={open}
      onClose={() => {
        handleClose();
      }}
      closeAfterTransition
    >
      <Fade in={open}>
        <Box
          component={Paper}
          sx={{
            position: 'absolute' as const,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '75%',
            outline: 'none',
            overflowY: 'scroll',
            height: '840px',
          }}
        >
          <IconButton
            sx={{
              position: 'absolute',
              float: 'right',
              top: 10,
              right: 10,
            }}
            onClick={() => {
              handleClose();
            }}
          >
            <CloseIcon />
          </IconButton>
          <RequestForInformation
            url={`dashboard/request-for-information?department_name=${selectedTab}`}
            handleClose={handleClose}
          >
            <Box sx={{ borderBottom: 1, borderColor: '#E7E7E7', mb: 1 }}>
              <Tabs
                value={selectedTab}
                onChange={handleTabChange}
                variant='scrollable'
                scrollButtons='auto'
                sx={{
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontSize: '16px',
                    fontWeight: 400,
                    color: '#878787',
                  },
                  '& .MuiButtonBase-root.Mui-selected': {
                    color: '#00CECF',
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: '#00CECF',
                  },
                }}
              >
                {tabs.map((tab: string) => (
                  <Tab
                    label={getLocalizedDepartmentName(tab)}
                    value={tab}
                    key={tab}
                    wrapped
                  />
                ))}
              </Tabs>
            </Box>
          </RequestForInformation>
        </Box>
      </Fade>
    </Modal>
  );
};

const extractUniqueStatuses = (dataFromApiParameter: StatusObject[]) => {
  return dataFromApiParameter.reduce((all, item) => {
    const statuses = [...all, ...item.data.map((v) => v.rfi_status)];
    // creating a Set removes string duplicates. and turing it back into an array
    return Array.from(new Set(statuses));
  }, [] as Array<string>);
};

const transformStatusDataForChart = (
  dataFromApiParameter: StatusObject[],
  colorsParameter: RFIByStatusColorType,
  propertiesParameter: string[] = [],
  keyLabelParameter: KeyProps = {},
  colorKeyLabelParameter: KeyProps = {},
  modifiedDataParameter: LineChartDataset[] = [],
) => {
  dataFromApiParameter.forEach((item: StatusObject) => {
    const modifiedItem: LineChartDataset = { label: item.label };
    propertiesParameter.forEach((property) => {
      const foundItem = item.data.find(
        (subItem) => subItem.rfi_status === property,
      );
      modifiedItem[property] = foundItem ? foundItem.count : 0;
      keyLabelParameter[property] = property;
      colorKeyLabelParameter[property] = colorsParameter[property];
    });
    modifiedDataParameter.push(modifiedItem);
  });
};

const RFIByStatus = ({ filterBy, date, dateEnd, tenantId }: FilterProps) => {
  const intl = useIntl();
  // will be added again once supporter in the backend
  // const selectedChannel = useIntakeChannel().intakeChannelData.selectedChannel;
  const link = 'dashboard/request-for-information-by-status';
  const [reportsDataList, setReportsDataList] = useState<LineChartDataset[]>(
    [],
  );
  const [colorLabel, setColorLabel] = useState<KeyProps>({});
  const [keyToLabel, setKeyToLabel] = useState<KeyProps>({});

  const { data, error, isLoading } = useSWR(
    getUrlString(link, filterBy, date, dateEnd, tenantId, null),
    {
      revalidateOnFocus: true,
    },
  );

  const transformDataByStatus = (dataFromApi: StatusObject[]) => {
    const modifiedData: LineChartDataset[] = [];
    const properties = extractUniqueStatuses(dataFromApi);
    const keyLabel: KeyProps = {};
    const colorKeyLabel: KeyProps = {};
    const colors: RFIByStatusColorType = {
      Pending: '#FFC700',
      Responded: '#2449FC',
      Viewed: '#00CECF',
      Closed: '#FF6A54',
    };

    transformStatusDataForChart(
      dataFromApi,
      colors,
      properties,
      keyLabel,
      colorKeyLabel,
      modifiedData,
    );

    // Check whether the objects in the array has two or more properties
    // If the object has less than two properties it indicates that there is no data available
    const hasTwoOrMoreProperties = modifiedData.every(
      (item) => Object.keys(item).length >= 2,
    );

    return {
      data: hasTwoOrMoreProperties ? modifiedData : [],
      keyToLabel: keyLabel,
      colors: colorKeyLabel,
    };
  };

  useEffect(() => {
    if (!isLoading && !error && data) {
      if (filterBy === 'month' && date.length > 0 && dateEnd.length > 0) {
        const start = dayjs(date);
        const end = dayjs(dateEnd);
        const startFormatted = start.format('MMM. D');
        const endFormatted = end.format('MMM. D');
        const transformedData = transformDataByStatus([
          {
            label: `${startFormatted} - ${endFormatted}`,
            data: data,
          },
        ]);
        setReportsDataList(transformedData.data);
        setKeyToLabel(transformedData.keyToLabel);
        setColorLabel(transformedData.colors);
      } else {
        const transformedData = transformDataByStatus(data);
        setReportsDataList(transformedData.data);
        setKeyToLabel(transformedData.keyToLabel);
        setColorLabel(transformedData.colors);
      }
    }
  }, [data, error, isLoading, filterBy, date, dateEnd]);

  return (
    <>
      {isLoading && <LoadingComponent isMinHeight />}
      {error && (
        <Typography
          sx={{
            fontSize: '14px',
            color: 'red',
            textAlign: 'left',
          }}
        >
          {error?.message ||
            intl.formatMessage({ defaultMessage: 'Something went wrong' })}
        </Typography>
      )}
      {!isLoading && !error && reportsDataList.length !== 0 && (
        <Stack direction='row' alignItems='center' spacing={2} width='100%'>
          <Typography
            sx={{
              ...headerText,
              fontSize: '14px',
            }}
          >
            <FormattedMessage defaultMessage='Total' />
          </Typography>
          <ReportsLineChart
            reportsDataList={reportsDataList}
            keyToLabel={keyToLabel}
            colors={colorLabel}
          />
        </Stack>
      )}
      {!isLoading && !error && reportsDataList.length === 0 && (
        <NoDataFound
          message={intl.formatMessage({ defaultMessage: 'No data found' })}
        />
      )}
    </>
  );
};

const RFIByDepartment = ({ filter, listRfiProps }: ListRFIProps) => {
  const intl = useIntl();
  // will be added again once supporter in the backend
  // const selectedChannel = useIntakeChannel().intakeChannelData.selectedChannel;
  const link = 'dashboard/request-for-information-by-department';
  const [reportsDataList, setReportsDataList] = useState<LineChartDataset[]>(
    [],
  );
  const [colorLabel, setColorLabel] = useState<KeyProps>({});
  const [keyToLabel, setKeyToLabel] = useState<KeyProps>({});
  const [unlocalizedKeyToLabel, setUnlocalizedKeyToLabel] = useState<KeyProps>(
    {},
  );
  const { getLocalizedDepartmentName } = useLocalizedDepartmentData();

  const { data, error, isLoading } = useSWR(
    getUrlString(
      link,
      filter.filterBy,
      filter.date,
      filter.dateEnd,
      filter.tenantId,
      null,
    ),
    {
      revalidateOnFocus: true,
    },
  );

  const extractUniqueDepartmentNames = (
    dataFromApiParameter: DepartmentObject[],
    propertiesParameter: string[] = [],
    unlocalizedPropertiesParameter: string[] = [],
  ) => {
    dataFromApiParameter.forEach((item: DepartmentObject) => {
      item.data.forEach((subItem: LineChartObjectByDepartment) => {
        if (!propertiesParameter.includes(subItem.rfi_department_name)) {
          propertiesParameter.push(
            getLocalizedDepartmentName(subItem.rfi_department_name),
          );
        }
        if (
          !unlocalizedPropertiesParameter.includes(subItem.rfi_department_name)
        ) {
          unlocalizedPropertiesParameter.push(subItem.rfi_department_name); // Add unlocalized name
        }
      });
    });
  };

  function transformDepartmentDataForChart(
    dataFromApiParameter: DepartmentObject[],
    colorsParameter: string[],
    modifiedDataParameter: LineChartDataset[] = [],
    unlocalizedPropertiesParameter: string[] = [],
    keyLabelParameter: KeyProps = {},
    unlocalizedKeyLabelParameter: KeyProps = {},
    colorKeyLabelParameter: KeyProps = {},
    propertiesParameter: string[] = [],
  ) {
    dataFromApiParameter.forEach((item: DepartmentObject) => {
      const modifiedItem: LineChartDataset = { label: item.label };
      propertiesParameter.forEach((property, index) => {
        const colorIndex = index % colorsParameter.length;
        const foundItem = item.data.find(
          (subItem) =>
            getLocalizedDepartmentName(subItem.rfi_department_name) ===
            property,
        );

        modifiedItem[property] = foundItem ? foundItem.count : 0;
        keyLabelParameter[property] = property;
        colorKeyLabelParameter[property] = colorsParameter[colorIndex];
      });
      unlocalizedPropertiesParameter.forEach((unlocalizedProperty) => {
        unlocalizedKeyLabelParameter[unlocalizedProperty] = unlocalizedProperty; // Map unlocalized names
      });
      modifiedDataParameter.push(modifiedItem);
    });
  }

  const transformDataByDepartment = useCallback(
    (dataFromApi: DepartmentObject[]) => {
      const colors = [
        '#021885',
        '#051D91',
        '#09229D',
        '#102CB5',
        '#1D3FE4',
        '#4262FC',
        '#7F94FD',
        '#9DADFE',
        '#003538',
        '#065B5B',
        '#008D8A',
        '#00B2AD',
        '#00DCD0',
        '#08F9EB',
        '#3FFFF5',
        '#84FFF9',
        '#C2FFFB',
      ];
      const modifiedData: LineChartDataset[] = [];
      const properties: string[] = [];
      const unlocalizedProperties: string[] = [];
      const keyLabel: KeyProps = {};
      const unlocalizedKeyLabel: KeyProps = {};
      const colorKeyLabel: KeyProps = {};

      extractUniqueDepartmentNames(
        dataFromApi,
        properties,
        unlocalizedProperties,
      );

      transformDepartmentDataForChart(
        dataFromApi,
        colors,
        modifiedData,
        unlocalizedProperties,
        keyLabel,
        unlocalizedKeyLabel,
        colorKeyLabel,
        properties,
      );

      // Check whether the objects in the array has two or more properties
      // If the object has less than two properties it indicates that there is no data available
      const hasTwoOrMoreProperties = modifiedData.every(
        (item) => Object.keys(item).length >= 2,
      );

      return {
        data: hasTwoOrMoreProperties ? modifiedData : [],
        keyToLabel: keyLabel,
        unlocalizedKeyToLabel: unlocalizedKeyLabel,
        colors: colorKeyLabel,
      };
    },
    [getLocalizedDepartmentName], //eslint-disable-line
  );

  useEffect(() => {
    const setData = (value: {
      data: LineChartDataset[];
      keyToLabel: KeyProps;
      unlocalizedKeyToLabel: KeyProps;
      colors: KeyProps;
    }) => {
      setReportsDataList(value.data);
      setKeyToLabel(value.keyToLabel);
      setUnlocalizedKeyToLabel(value.unlocalizedKeyToLabel);
      setColorLabel(value.colors);
    };

    if (!isLoading && !error && data) {
      if (
        filter.filterBy === 'month' &&
        filter.date.length > 0 &&
        filter.dateEnd.length > 0
      ) {
        const start = dayjs(filter.date);
        const end = dayjs(filter.dateEnd);
        const startFormatted = start.format('MMM. D');
        const endFormatted = end.format('MMM. D');
        const transformedData = transformDataByDepartment([
          {
            label: `${startFormatted} - ${endFormatted}`,
            data: data,
          },
        ]);
        setData(transformedData);
      } else {
        const transformedData = transformDataByDepartment(data);
        setData(transformedData);
      }
    }
  }, [
    data,
    error,
    filter.date,
    filter.dateEnd,
    filter.filterBy,
    isLoading,
    transformDataByDepartment,
  ]);

  return (
    <>
      {isLoading && <LoadingComponent isMinHeight />}
      {error && (
        <Typography level='body-md' color='danger'>
          {error?.message ||
            intl.formatMessage({ defaultMessage: 'Something went wrong' })}
        </Typography>
      )}
      {!isLoading && !error && reportsDataList.length !== 0 && (
        <>
          <div className='flex flex-row items-center gap-2 w-full'>
            <Typography level='body-md'>
              <FormattedMessage defaultMessage='Total' />
            </Typography>
            <ReportsLineChart
              reportsDataList={reportsDataList}
              keyToLabel={keyToLabel}
              colors={colorLabel}
            />
          </div>
          {listRfiProps.open && reportsDataList.length !== 0 && (
            <ListRFIByDepartment
              open={listRfiProps.open}
              handleClose={listRfiProps.handleClose}
              departments={unlocalizedKeyToLabel}
            />
          )}
        </>
      )}
      {!isLoading && !error && reportsDataList.length === 0 && (
        <NoDataFound message='No data found' />
      )}
    </>
  );
};

const ReportsRFI = () => {
  const intl = useIntl();
  const { selectedTenant } = usePartnerContext();
  const form = useDashoardFilter();

  /* const availableMetrics = useAvailableMetrics({
    category: 'RFI',
  });

  const checkIfMetricIsAvailable = (metric: ReportFilters) => {
    if (metric === ReportFilters.DEPARTMENT) {
      return !!availableMetrics.find(
        (item) => item.name === 'RFI by Department',
      );
    }
    if (metric === ReportFilters.STATUS) {
      return !!availableMetrics.find((item) => item.name === 'RFI by Status');
    }
    return false;
  }; */

  const from = form.watch('date_range').from;
  const to = form.watch('date_range').to;

  const [filterRFI, setFilterRFI] = useState<ReportFilters>(
    ReportFilters.STATUS,
  );
  const [viewRFI, setViewRFI] = useState(false);

  const intlFormatFilterMessage = (value: string) => {
    return value === ReportFilters.STATUS
      ? intl.formatMessage({ defaultMessage: 'Status' })
      : intl.formatMessage({ defaultMessage: 'by Department' });
  };

  const handleClose = () => {
    setViewRFI(false);
  };

  return (
    <Card className='shadow-sm'>
      <div className='flex flex-row justify-between'>
        <Typography level='body-md'>
          <FormattedMessage defaultMessage='Request for Information' />
        </Typography>
        <div className='flex flex-row gap-2'>
          {filterRFI === ReportFilters.DEPARTMENT && (
            <Button
              variant='solid'
              color='primary'
              onClick={() => setViewRFI(true)}
            >
              <FormattedMessage defaultMessage='View RFI' />
            </Button>
          )}
          {/* Filter */}
          <Select
            value={filterRFI}
            onChange={(_, value) => {
              if (value) {
                setFilterRFI(value as ReportFilters);
              }
            }}
          >
            {Object.values(ReportFilters).map((value) => {
              return (
                <Option key={value} value={value}>
                  {intlFormatFilterMessage(value)}
                </Option>
              );
            })}
          </Select>
        </div>
      </div>
      {filterRFI === ReportFilters.STATUS ? (
        <CheckMetricWrapper metricCategory='RFI' metricName='RFI by Status'>
          <RFIByStatus
            filterBy='day'
            date={from ? from.toISOString() : ''}
            dateEnd={to ? to.toISOString() : ''}
            tenantId={selectedTenant.id}
          />
        </CheckMetricWrapper>
      ) : (
        <CheckMetricWrapper metricCategory='RFI' metricName='RFI by Department'>
          <RFIByDepartment
            filter={{
              filterBy: 'day',
              date: from ? from.toISOString() : '',
              dateEnd: to ? to.toISOString() : '',
              tenantId: selectedTenant.id,
            }}
            listRfiProps={{
              handleClose: handleClose,
              open: viewRFI,
            }}
          />
        </CheckMetricWrapper>
      )}
    </Card>
  );
};

export default ReportsRFI;
